import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert, Navbar } from 'react-bootstrap';
import ApprovalsPage from './components/ApprovalsPage';
import DashboardPage from './components/DashboardPage';
import MyReportsPage from './components/MyReportsPage';
import NetworkConsumptionReport from './components/NetworkConsumptionReport';
import CfoBurnReport from './components/CfoBurnReport';
import SettingsPage from './components/SettingsPage';
import EnquiryCallPage from './components/EnquiryCallPage';
import Sidebar from './components/Sidebar';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import logoImage from './assets/HiveMind_Logo.png';
import { initTheme, setTheme, toggleTheme, getTheme } from './utils/theme';
//comment to force commit
function App() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [token, setToken] = useState(null);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' or 'approvals'
    const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
    const [userEmail, setUserEmail] = useState('');
    const [userDisplayName, setUserDisplayName] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(getTheme());
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Initialize theme on mount
    useEffect(() => {
        const theme = getTheme();
        initTheme();
        setCurrentTheme(theme);
    }, []);

    // Theme toggle handler
    const handleThemeToggle = () => {
        const newTheme = toggleTheme();
        setCurrentTheme(newTheme);
    };

    // Prevent body scroll when sidebar is open on mobile
    useEffect(() => {
        if (isSidebarOpen && window.innerWidth < 992) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isSidebarOpen]);

    const fetchPendingApprovalCount = async () => {
        try {
            const response = await fetch('/api/approvals/index.php?status=Open&per_page=1', {
                headers: {
                    'Authorization': 'Bearer ' + token
                }
            });
            const data = await response.json();
            if (data.pagination) {
                setPendingApprovalCount(data.pagination.total_bookings);
            }
        } catch (err) {
        }
    };

    // Fetch pending approval count when dashboard loads
    useEffect(() => {
        if (token && currentView === 'dashboard') {
            fetchPendingApprovalCount();
        }
    }, [token, currentView]);

    // Handle magic link token and reset password token on page load
    useEffect(() => {
        const handleMagicLink = async () => {
            // Check URL for reset password token first
            const urlParams = new URLSearchParams(window.location.search);
            
            // Check if this is a password reset token (not magic link)
            // Reset password links use 'reset_token' parameter
            if (urlParams.has('reset_token')) {
                setShowResetPassword(true);
                // Keep the token in URL for ResetPassword component
                return;
            }
            
            // Check URL for magic token parameter
            const magicToken = urlParams.get('magic_token');
            
            // Only process if we have a token and user is not already logged in
            if (magicToken && !isLoggedIn) {
                try {
                    setLoginLoading(true);
                    setError(null);
                    
                    // Call validation endpoint
                    const response = await fetch(`/api/auth/validate-magic-link.php?token=${encodeURIComponent(magicToken)}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });
                    
                    // Check if response is ok before parsing JSON
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({ error: 'Failed to validate magic link' }));
                        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    
                    if (data.success && data.token) {
                        // Store JWT token and user info
                        setToken(data.token);
                        setUserEmail(data.user.email);
                        setUserDisplayName(data.user.displayName || data.user.username || 'User');
                        setIsLoggedIn(true);
                        
                        // Redirect to approvals page (or specified redirect_to)
                        const redirectTo = data.redirect_to || 'approvals';
                        setCurrentView(redirectTo);
                        
                        // Apply theme from cookie
                        const theme = getTheme();
                        setTheme(theme);
                        setCurrentTheme(theme);
                        
                        // Clean URL - remove token parameter
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    } else if (data.expired && data.new_link_sent) {
                        // Expired token - new link sent
                        setError(data.message || 'Your previous link expired. A new link has been sent to your email address.');
                        // Clean URL
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    } else {
                        setError(data.error || 'Invalid magic link');
                        // Clean URL even on error
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    }
                } catch (err) {
                    setError('Failed to validate magic link: ' + err.message);
                    // Clean URL on error
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, document.title, newUrl);
                } finally {
                    setLoginLoading(false);
                }
            }
        };
        
        handleMagicLink();
    }, []); // Run once on mount

    const handleLogin = async () => {
        if (!username || !password) {
            setError('Please enter username and password');
            return;
        }

        setLoginLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/auth/login.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: username,
                    password: password,
                }),
            });

            const data = await response.json();

            if (data.success && data.token) {
                setToken(data.token);
                setUserEmail(data.user.email);
                setUserDisplayName(data.user.displayName || data.user.username || 'User');
                setIsLoggedIn(true);
                setCurrentView('dashboard');
                // Apply theme from cookie
                const theme = getTheme();
                setTheme(theme);
                setCurrentTheme(theme);
            } else {
                setError(data.error || 'Login failed');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoginLoading(false);
        }
    };

    const fetchUsers = async (authToken) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/users/', {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        setToken(null);
        setUserEmail('');
        setUserDisplayName('');
        setIsLoggedIn(false);
        setCurrentView('dashboard');
        setUsers([]);
        setUsername('');
        setPassword('');
        setError(null);
        setShowForgotPassword(false);
        setShowResetPassword(false);
        // Theme persists via cookie, no need to remove
    };

    const handleBackToLogin = () => {
        setShowForgotPassword(false);
        setShowResetPassword(false);
        setError(null);
        // Clean URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
    };

    const handleSettings = () => {
        setCurrentView('settings');
    };

    // Show login form if not logged in
    if (!isLoggedIn) {
        // Show reset password page if reset token is present
        if (showResetPassword) {
            const urlParams = new URLSearchParams(window.location.search);
            const resetToken = urlParams.get('reset_token') || urlParams.get('token');
            return <ResetPassword onBackToLogin={handleBackToLogin} token={resetToken} />;
        }
        
        // Show forgot password page
        if (showForgotPassword) {
            return <ForgotPassword onBackToLogin={handleBackToLogin} />;
        }
        
        // Show login form
        return (
            <div className="login-page-container">
                {/* Background blur effects */}
                <div className="bg-blur-indigo"></div>
                <div className="bg-blur-fuchsia"></div>
                
                <Container className="py-5 login-page-content">
                    <Row className="justify-content-center">
                        <Col md={6} lg={4}>
                            <Card className="glass">
                                <Card.Body>
                                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                                        <img src={logoImage} alt="HiveMind Logo" className="login-logo" style={{ width: '90px', height: 'auto' }} />
                                    </div>
                                    <Card.Title className="text-center mb-4 login-title">Client Desk</Card.Title>
                                    <Card.Text className="text-center mb-4 login-subtitle">
                                        Please login to access the system
                                    </Card.Text>
                                    
                                    <Form>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="login-label">Username or Email</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter username or email"
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                autoCapitalize="none"
                                                className="login-input"
                                            />
                                        </Form.Group>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label className="login-label">Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Enter password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="login-input"
                                            />
                                        </Form.Group>
                                        
                                        <div className="text-end mb-3">
                                            <Button
                                                variant="link"
                                                onClick={() => setShowForgotPassword(true)}
                                                className="login-link"
                                                style={{ padding: 0, fontSize: '0.875rem' }}
                                            >
                                                Forgot Password?
                                            </Button>
                                        </div>
                                        
                                        {error && (
                                            <Alert variant="danger" className="mb-3">
                                                {error}
                                            </Alert>
                                        )}
                                        
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            className="w-100"
                                            onClick={handleLogin}
                                            disabled={loginLoading}
                                            style={{
                                                background: '#6366f1',
                                                borderColor: '#6366f1'
                                            }}
                                        >
                                            {loginLoading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Logging in...
                                                </>
                                            ) : (
                                                'Login'
                                            )}
                                        </Button>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // Show loading state
    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" size="lg" />
                <p className="mt-3">Loading users from Hivemind database...</p>
            </Container>
        );
    }

    // Show error state
    if (error) {
        return (
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={6}>
                        <Alert variant="danger">
                            <Alert.Heading>Error</Alert.Heading>
                            <p>{error}</p>
                            <hr />
                            <Button variant="outline-danger" onClick={() => fetchUsers(token)}>
                                Retry
                            </Button>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    // Main application - show different views based on currentView state
    if (currentView === 'approvals') {
        return (
            <>
                <Sidebar 
                    isOpen={isSidebarOpen} 
                    onToggle={toggleSidebar} 
                    activeView={currentView === 'network-consumption' || currentView === 'cfo-burn' ? 'reports' : currentView}
                    onNavigateToDashboard={() => setCurrentView('dashboard')} 
                    onNavigateToApprovals={() => setCurrentView('approvals')}
                    onNavigateToReports={() => setCurrentView('reports')}
                    onNavigateToSettings={handleSettings} 
                    onLogout={handleLogout}
                    onThemeToggle={handleThemeToggle}
                    currentTheme={currentTheme}
                />
                <div className="main-content-with-sidebar">
                    <ApprovalsPage 
                        onBack={() => setCurrentView('dashboard')} 
                        token={token}
                        onLogout={handleLogout}
                        onSettings={handleSettings}
                    />
                </div>
            </>
        );
    }

    if (currentView === 'reports') {
        return (
            <>
                <Sidebar 
                    isOpen={isSidebarOpen} 
                    onToggle={toggleSidebar} 
                    activeView={currentView === 'network-consumption' || currentView === 'cfo-burn' ? 'reports' : currentView}
                    onNavigateToDashboard={() => setCurrentView('dashboard')} 
                    onNavigateToApprovals={() => setCurrentView('approvals')}
                    onNavigateToReports={() => setCurrentView('reports')}
                    onNavigateToSettings={handleSettings} 
                    onLogout={handleLogout}
                    onThemeToggle={handleThemeToggle}
                    currentTheme={currentTheme}
                />
                <div className="main-content-with-sidebar">
                    <MyReportsPage
                        onBack={() => setCurrentView('dashboard')}
                        onNavigateToNetworkConsumption={() => setCurrentView('network-consumption')}
                        onNavigateToCfoBurn={() => setCurrentView('cfo-burn')}
                        onLogout={handleLogout}
                        onSettings={handleSettings}
                    />
                </div>
            </>
        );
    }

        if (currentView === 'network-consumption') {
            return (
                <>
                    <Sidebar 
                        isOpen={isSidebarOpen} 
                        onToggle={toggleSidebar} 
                        activeView={currentView === 'network-consumption' || currentView === 'cfo-burn' ? 'reports' : currentView}
                        onNavigateToDashboard={() => setCurrentView('dashboard')} 
                        onNavigateToApprovals={() => setCurrentView('approvals')}
                        onNavigateToReports={() => setCurrentView('reports')}
                        onNavigateToSettings={handleSettings} 
                        onLogout={handleLogout}
                        onThemeToggle={handleThemeToggle}
                        currentTheme={currentTheme}
                    />
                    <div className="main-content-with-sidebar">
                        <NetworkConsumptionReport
                            onBack={() => setCurrentView('reports')}
                            token={token}
                            onLogout={handleLogout}
                            onSettings={handleSettings}
                        />
                    </div>
                </>
            );
        }

        if (currentView === 'cfo-burn') {
            return (
                <>
                    <Sidebar 
                        isOpen={isSidebarOpen} 
                        onToggle={toggleSidebar} 
                        activeView={currentView === 'network-consumption' || currentView === 'cfo-burn' ? 'reports' : currentView}
                        onNavigateToDashboard={() => setCurrentView('dashboard')} 
                        onNavigateToApprovals={() => setCurrentView('approvals')}
                        onNavigateToReports={() => setCurrentView('reports')}
                        onNavigateToSettings={handleSettings} 
                        onLogout={handleLogout}
                        onThemeToggle={handleThemeToggle}
                        currentTheme={currentTheme}
                    />
                    <div className="main-content-with-sidebar">
                        <CfoBurnReport
                            onBack={() => setCurrentView('reports')}
                            token={token}
                            onLogout={handleLogout}
                            onSettings={handleSettings}
                        />
                    </div>
                </>
            );
        }

    // Default dashboard view or settings page
    if (currentView === 'settings') {
        return (
            <>
                <Sidebar 
                    isOpen={isSidebarOpen} 
                    onToggle={toggleSidebar} 
                    activeView={currentView === 'network-consumption' || currentView === 'cfo-burn' ? 'reports' : currentView}
                    onNavigateToDashboard={() => setCurrentView('dashboard')} 
                    onNavigateToApprovals={() => setCurrentView('approvals')}
                    onNavigateToReports={() => setCurrentView('reports')}
                    onNavigateToSettings={handleSettings} 
                    onLogout={handleLogout}
                    onThemeToggle={handleThemeToggle}
                    currentTheme={currentTheme}
                />
                <div className="main-content-with-sidebar">
                    <SettingsPage
                        onBack={() => setCurrentView('dashboard')}
                        token={token}
                        onLogout={handleLogout}
                        userEmail={userEmail}
                    />
                </div>
            </>
        );
    }

    if (currentView === 'enquiry-call') {
        return (
            <>
                <Sidebar 
                    isOpen={isSidebarOpen} 
                    onToggle={toggleSidebar} 
                    activeView={currentView === 'network-consumption' || currentView === 'cfo-burn' ? 'reports' : currentView}
                    onNavigateToDashboard={() => setCurrentView('dashboard')} 
                    onNavigateToApprovals={() => setCurrentView('approvals')}
                    onNavigateToReports={() => setCurrentView('reports')}
                    onNavigateToSettings={handleSettings} 
                    onLogout={handleLogout}
                    onThemeToggle={handleThemeToggle}
                    currentTheme={currentTheme}
                />
                <div className="main-content-with-sidebar">
                    <EnquiryCallPage
                        onBack={() => setCurrentView('dashboard')}
                        token={token}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <Sidebar 
                isOpen={isSidebarOpen} 
                onToggle={toggleSidebar} 
                activeView={currentView === 'network-consumption' || currentView === 'cfo-burn' ? 'reports' : currentView}
                onNavigateToDashboard={() => setCurrentView('dashboard')} 
                onNavigateToApprovals={() => setCurrentView('approvals')}
                onNavigateToReports={() => setCurrentView('reports')}
                onNavigateToSettings={handleSettings} 
                onLogout={handleLogout}
                onThemeToggle={handleThemeToggle}
                currentTheme={currentTheme}
            />
            <div className="main-content-with-sidebar">
                <DashboardPage 
                    onNavigateToApprovals={() => setCurrentView('approvals')}
                    onNavigateToReports={() => setCurrentView('reports')}
                    onNavigateToEnquiryCall={() => setCurrentView('enquiry-call')}
                    onLogout={handleLogout}
                    onSettings={handleSettings}
                    pendingApprovalCount={pendingApprovalCount}
                    userDisplayName={userDisplayName}
                />
            </div>
        </>
    );
}

export default App;
